import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, mapTo, mergeMap } from 'rxjs/operators';
import { ArrayHelper } from '../../../helpers/arrayHelper';
import { IdHelper } from '../../../helpers/idHelper';
import { MapHelper } from '../../../helpers/mapHelper';
import { ObjectHelper } from '../../../helpers/objectHelper';
import { StoreHelper } from '../../../helpers/storeHelper';
import { StringHelper } from '../../../helpers/stringHelper';
import { EPrefix } from '../../../../../../apps/idl/src/model/EPrefix';
import { EDatabaseRole } from '../../../model/store/EDatabaseRole';
import { IStoreDocument } from '../../../model/store/IStoreDocument';
import { EntityLinkService } from '../../../services/security/entityLink.service';
import { Store } from '../../../services/store.service';
import { DmsDocument } from '../model/dms-document';
import { IDmsDocument } from '../model/IDmsDocument';
import { IDmsDocumentData } from '../model/IDmsDocumentData';

@Injectable()
export class DmsExternalLinkerService {

	//#region FIELDS

	private static readonly C_LOG_ID = "DMS.EXT.LINK.S::";

	//#endregion

	//#region METHODS

	constructor(private readonly isvcEntityLink: EntityLinkService, private readonly isvcStore: Store) { }

	/** Lie des documents à une entité. Il faut donner tous les fichiers liés car ceux qui ne sont pas indiqués seront supprimés.
	 * @param poEntity 
	 * @param paFiles 
	 */
	public linkFilesToEntity(poEntity: IStoreDocument, paFiles: IDmsDocumentData[]): Observable<boolean> {
		const laDmsDocuments: DmsDocument[] = paFiles.map((poFile: IDmsDocumentData) =>
			DmsExternalLinkerService.generateDmsDocument(poFile)
		);

		return this.getEntityLinkedDocuments(poEntity).pipe(
			mergeMap((paOldDmsDocuments: IDmsDocument[]) => {
				const laDmsDocumentsToRemove: IDmsDocument[] = ArrayHelper.getDifferences(paOldDmsDocuments, laDmsDocuments, (poDocA: IDmsDocument, poDocB: DmsDocument) => poDocA._id === poDocB._id);
				const laDmsDocumentsToAdd: DmsDocument[] = ArrayHelper.getDifferences(laDmsDocuments, paOldDmsDocuments, (poDocA: DmsDocument, poDocB: IDmsDocument) => poDocA._id === poDocB._id);

				this.isvcEntityLink.cacheLinkToAdd(poEntity, laDmsDocumentsToAdd.map((poDoc: DmsDocument) => {
					try {
						StoreHelper.updateDocumentCacheData(poDoc, { databaseId: StoreHelper.getDatabaseIdFromCacheData(poEntity) });
						return this.isvcEntityLink.buildEntity(poDoc);
					}
					catch (poError) {
						console.error(`${DmsExternalLinkerService.C_LOG_ID}`, poError);
						return undefined;
					}
				}));
				this.isvcEntityLink.cacheLinkToRemove(poEntity, laDmsDocumentsToRemove.map((poDoc: IDmsDocument) => {
					poDoc._deleted = true;
					try {
						return this.isvcEntityLink.buildEntity(poDoc);
					}
					catch (poError) {
						console.error(`${DmsExternalLinkerService.C_LOG_ID}`, poError);
						return undefined;
					}
				}));

				const laDmsDocumentsToUpdate: DmsDocument[] = ArrayHelper.intersection(laDmsDocuments, paOldDmsDocuments, (poDocA: DmsDocument, poDocB: IDmsDocument) => {
					if (poDocA._id === poDocB._id) {
						poDocA._rev = poDocB._rev;
						StoreHelper.updateDocumentCacheData(poDocA, StoreHelper.getDocumentCacheData(poDocB));
						return !ObjectHelper.areEquals(poDocA, poDocB);
					}
					return false;
				});

				return this.isvcEntityLink.saveEntityLinks(poEntity).pipe(
					mergeMap(() =>
						this.isvcStore.putMultipleDocuments([...laDmsDocumentsToAdd, ...laDmsDocumentsToUpdate, ...laDmsDocumentsToRemove], ArrayHelper.getFirstElement(this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.workspace)))
					),
					mapTo(true)
				);
			})
		);
	}

	public static generateDmsDocumentId(poFile: IDmsDocumentData): string {
		if (!poFile)
			return undefined;

		return IdHelper.buildId(this.generatePrefix(poFile.type), poFile.guid);
	}

	public static generateDmsDocument(poFile: IDmsDocumentData): DmsDocument {
		return new DmsDocument(({ _id: this.generateDmsDocumentId(poFile), ...poFile }));
	}

	private static generatePrefix(psType?: string): EPrefix {
		return `${EPrefix.dmsDoc}${!StringHelper.isBlank(psType) ? `${psType}_` : ""}` as EPrefix;
	}

	/** Récupère les documents liés à une entité.
	 * @param poEntity
	 * @param psType Type de document `optional`
	 * @returns Un observable qui retourne les documents liés.
	 */
	public getEntityLinkedDocuments(poEntity?: IStoreDocument | string, psType?: string): Observable<DmsDocument[]> {
		return this.isvcEntityLink.getLinkedEntities(poEntity, DmsExternalLinkerService.generatePrefix(psType)).pipe(
			map(((paDocs: IDmsDocument[]) => (paDocs ?? []).map((poDmsDocument: IDmsDocument) => new DmsDocument(poDmsDocument))))
		);
	}

	/** Récupère les documents liés à un ensemble d'entités.
	 * @param paEntities
	 * @param psType Type de document `optional`
	 * @returns Un observable qui retourne les documents liés.
	 */
	public getEntitiesLinkedDocuments(paEntities?: IStoreDocument[] | string[], psType?: string): Observable<Map<string, DmsDocument[]>> {
		return this.isvcEntityLink.getLinkedEntities<IDmsDocument>(paEntities, DmsExternalLinkerService.generatePrefix(psType)).pipe(
			map(((poDocsByIds: Map<string, IDmsDocument[]>) => MapHelper.map(poDocsByIds, (paDocs: IDmsDocument[]) => (paDocs ?? []).map((poDmsDocument: IDmsDocument) => new DmsDocument(poDmsDocument)))))
		);
	}

	//#endregion

}
